import React from 'react';
import { SchematicShell } from './SchematicPrimitives';
import {
  catenarySagPoints,
  evenSpacedDistances,
  PENNANT_CURVE_SEGMENTS,
  PENNANT_DROOP_FRAC,
  PENNANT_FLAG_WIDTH_FRAC,
  PENNANT_SPACING,
  walkPolyline,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Pennant garland, 2D.
 *
 * This is the piece whose whole shape IS the sag, so the curve and the
 * flag-spacing walk are the SHARED pure ones rather than a second sine copied
 * across — a garland that sagged differently in plan and elevation would be
 * the drift `proceduralSpecs.js` exists to prevent, on the worst possible
 * candidate.
 *
 * Cord endpoints sit at the top of the box and the flags hang below, matching
 * the renderer's `baseY = height / 2` lift. SVG is +y DOWN, so the renderer's
 * negative sag becomes a positive offset here.
 */
export function DormPennantBannerSchematic({
  widthMeters: width,
  heightMeters: height,
  strokeColor,
  strokeWidth,
}) {
  const droop = height * PENNANT_DROOP_FRAC;
  const flagH = height - droop;
  const flagW = flagH * PENNANT_FLAG_WIDTH_FRAC;

  // The cord spans less than the full width: a flag centred on the last cord
  // point still extends half its own width past it.
  const span = Math.max(0, width - flagW);
  const points = catenarySagPoints(span, droop, PENNANT_CURVE_SEGMENTS);

  const toSvg = (p) => ({ x: width / 2 + p.x, y: -p.y });
  const cord = points
    .map((p, i) => {
      const s = toSvg(p);
      return `${i === 0 ? 'M' : 'L'} ${s.x} ${s.y}`;
    })
    .join(' ');

  const flags = [];
  walkPolyline(points, evenSpacedDistances(points, PENNANT_SPACING), (x, y) => {
    const cx = width / 2 + x;
    const cy = -y;
    flags.push(
      `M ${cx - flagW / 2} ${cy} L ${cx + flagW / 2} ${cy} L ${cx} ${cy + flagH} Z`
    );
  });

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <path
        d={cord}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth * 0.6}
        vectorEffect="non-scaling-stroke"
      />
      {flags.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </SchematicShell>
  );
}
